/**
 * ChatPage - Full-page chat interface
 * Wide screens (md+): Two-column layout (conversations | messages)
 * Narrow screens: Single column with toggle between list and conversation
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthLayout from '../../../shared/components/layout/AuthLayout';
import { useChat, useConversationDraft } from '../context/ChatProvider';
import { ConversationList } from '../components/ConversationList';
import { ConversationHeader } from '../components/ConversationHeader';
import { MessageList } from '../components/MessageList';
import { MessageInput } from '../components/MessageInput';
import { NewChatModal } from '../components/NewChatModal';
import { ReconnectingChip } from '../../../shared/components/ui/ReconnectingChip';

export const ChatPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const friendIdParam = searchParams.get('friendId');
  const messageScrollRef = useRef<HTMLDivElement | null>(null);
  const {
    conversations,
    messagesByConversationId,
    activeConversationId,
    isLoading,
    loadingConversationId,
    isSendingMessage,
    error,
    openConversation,
    sendMessage,
    startNewChat,
    loadConversations,
    registerChatSurface,
    unregisterChatSurface,
  } = useChat();

  // Enable conversations polling when chat page is mounted, disable when unmounted
  useEffect(() => {
    loadConversations(); // Initial load
    registerChatSurface();
    
    return () => {
      unregisterChatSurface();
    };
  }, [loadConversations, registerChatSurface, unregisterChatSurface]);

  const [showConversationList, setShowConversationList] = useState(true);
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  
  // Track if we've processed the friendIdParam to avoid duplicate processing
  const processedFriendIdRef = useRef<string | null>(null);
  
  // Handle friendId query parameter - open conversation directly
  useEffect(() => {
    // Only process if we have a friendId param and haven't processed it yet
    if (!friendIdParam || processedFriendIdRef.current === friendIdParam) {
      return;
    }
    
    // Function to open conversation with friend
    const openConversationWithFriend = async () => {
      // Find existing conversation with this friend
      const existingConversation = conversations.find(
        (c) => c.friendUserId === friendIdParam
      );
      
      if (existingConversation) {
        // Open existing conversation
        await openConversation(existingConversation.id);
        // On mobile, hide the list and show the conversation
        // On desktop, keep the list visible (default behavior)
        const isNarrow = window.innerWidth < 768;
        if (isNarrow) {
          setShowConversationList(false);
        } else {
          // Desktop: Ensure list is visible
          setShowConversationList(true);
        }
        // Mark as processed and clear the query parameter
        processedFriendIdRef.current = friendIdParam;
        setSearchParams({});
      } else if (!isLoading) {
        // No existing conversation and loading is complete, create a new one
        try {
          await startNewChat(friendIdParam);
          // On mobile, hide the list and show the conversation
          // On desktop, keep the list visible (default behavior)
          const isNarrow = window.innerWidth < 768;
          if (isNarrow) {
            setShowConversationList(false);
          } else {
            // Desktop: Ensure list is visible
            setShowConversationList(true);
          }
          // Mark as processed and clear the query parameter
          processedFriendIdRef.current = friendIdParam;
          setSearchParams({});
        } catch (err) {
          console.error('Failed to start new chat:', err);
        }
      }
    };
    
    // If conversations are loaded, open immediately
    // Otherwise, wait for them to load (the effect will re-run when conversations change)
    if (conversations.length > 0 || !isLoading) {
      openConversationWithFriend();
    }
  }, [friendIdParam, conversations, isLoading, openConversation, startNewChat, setSearchParams]);
  
  // Reset processed friend ID when friendIdParam changes or is cleared
  useEffect(() => {
    if (!friendIdParam) {
      processedFriendIdRef.current = null;
    }
  }, [friendIdParam]);

  const activeConversation = conversations.find((c) => c.id === activeConversationId);
  const activeMessages = activeConversationId
    ? messagesByConversationId[activeConversationId] || []
    : [];
  const isLoadingMessages = activeConversationId === loadingConversationId;
  const { draft, setDraft } = useConversationDraft(activeConversationId);

  const handleSelectConversation = (conversationId: string) => {
    openConversation(conversationId);
    // On narrow screens (< md breakpoint), hide list when conversation opens
    const isNarrow = window.innerWidth < 768; // md breakpoint is 768px
    if (isNarrow) {
      setShowConversationList(false);
    }
  };

  const handleBackToList = () => {
    setShowConversationList(true);
  };

  const handleSendMessage = async (body: string) => {
    if (!activeConversationId) return;
    await sendMessage(activeConversationId, body);
  };

  const handleNewChat = async (friendUserId: string) => {
    await startNewChat(friendUserId);
    // On narrow screens, show conversation after creating
    const isNarrow = window.innerWidth < 768;
    if (isNarrow) {
      setShowConversationList(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col min-h-0">
        <div
          className="bg-white rounded-lg shadow-lg overflow-hidden"
          style={{ height: 'clamp(500px, calc(100vh - 120px), 750px)' }}
        >
          {/* Two-column layout on wide screens, single column on narrow */}
          <div className="grid grid-cols-1 md:grid-cols-2 h-full">
            {/* Left Column - Conversations List */}
            <div
              className={`${
                showConversationList ? 'flex' : 'hidden'
              } md:flex flex-col border-r border-gray-200 h-full min-h-0`}
            >
            {/* Header with New Chat button - Sticky at top */}
            <div className="p-4 border-b border-gray-200 bg-white flex-shrink-0 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
                <button
                  onClick={() => setIsNewChatModalOpen(true)}
                  className="p-1 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                  title="New Chat"
                  aria-label="Start new chat"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Conversations List - Scrollable */}
            <div
              data-conversation-list
              className="flex-1 min-h-0 overflow-y-auto chat-scrollable"
            >
              {/* Show error chip if there's an error but we have cached conversations */}
              {error && conversations.length > 0 && (
                <div className="px-4 pt-2 pb-2">
                  <ReconnectingChip message={error} onRetry={loadConversations} />
                </div>
              )}
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Right Column - Active Conversation */}
          <div
            className={`${
              showConversationList ? 'hidden' : 'flex'
            } md:flex flex-col h-full min-h-0 overflow-hidden`}
          >
            {activeConversation ? (
              <>
                {/* Conversation Header with Back Button (only on narrow screens) - Sticky at top */}
                <div className="md:hidden flex items-center space-x-3 p-4 border-b border-gray-200 bg-white flex-shrink-0 z-10">
                  <button
                    onClick={handleBackToList}
                    className="p-1 rounded-full hover:bg-gray-100 flex-shrink-0"
                    aria-label="Back to conversations"
                  >
                    <svg
                      className="w-5 h-5 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {activeConversation.friendAvatar ? (
                      <img
                        src={activeConversation.friendAvatar}
                        alt={activeConversation.friendName}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-gray-600">
                          {activeConversation.friendName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {activeConversation.friendName}
                      </h3>
                      {activeConversation.friendUsername && (
                        <p className="text-xs text-gray-500 truncate">
                          @{activeConversation.friendUsername}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Full Conversation Header (wide screens only) - Sticky at top */}
                <div className="hidden md:block flex-shrink-0 z-10">
                  <ConversationHeader conversation={activeConversation} />
                </div>

                {/* Messages - ONLY scroll container */}
                <div
                  ref={messageScrollRef}
                  className="flex-1 min-h-0 overflow-y-auto chat-scrollable"
                >
                  <MessageList
                    messages={activeMessages}
                    isLoading={isLoadingMessages}
                    scrollContainerRef={messageScrollRef}
                  />
                </div>

                {/* Message Input - Sticky at bottom */}
                <div className="flex-shrink-0 z-10 bg-white border-t border-gray-200">
                  <MessageInput
                    onSend={handleSendMessage}
                    value={draft}
                    onChange={setDraft}
                    isSending={isSendingMessage}
                    autoFocus={false}
                    disabled={activeConversation.isActive === false}
                    disabledMessage="You are no longer friends with this user. Messages cannot be sent."
                  />
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <svg
                    className="mx-auto h-16 w-16 text-gray-400 mb-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-sm mt-1">Or start a new chat with a friend</p>
                  <button
                    onClick={() => setIsNewChatModalOpen(true)}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    New Chat
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* New Chat Modal */}
      <NewChatModal
        isOpen={isNewChatModalOpen}
        onClose={() => setIsNewChatModalOpen(false)}
        onSelectFriend={handleNewChat}
      />
    </AuthLayout>
  );
};

export default ChatPage;
